import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** When set, ⌘K and the top-bar search button focus page-local search instead of the dialog. */
export type PageSearchHandler = () => void;

interface GlobalSearchContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
  registerPageSearchHandler: (handler: PageSearchHandler | null) => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(null);

export function GlobalSearchProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const pageSearchHandlerRef = useRef<PageSearchHandler | null>(null);

  const registerPageSearchHandler = useCallback(
    (handler: PageSearchHandler | null) => {
      pageSearchHandlerRef.current = handler;
    },
    [],
  );

  const runPageOrGlobalSearch = useCallback(() => {
    if (pageSearchHandlerRef.current) {
      pageSearchHandlerRef.current();
      return;
    }
    setOpen(true);
  }, []);

  const openSearch = runPageOrGlobalSearch;

  const closeSearch = useCallback(() => setOpen(false), []);

  const toggleSearch = useCallback(() => {
    if (pageSearchHandlerRef.current) {
      pageSearchHandlerRef.current();
      return;
    }
    setOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggleSearch();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [toggleSearch]);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      openSearch,
      closeSearch,
      toggleSearch,
      registerPageSearchHandler,
    }),
    [open, openSearch, closeSearch, toggleSearch, registerPageSearchHandler],
  );

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  );
}

export function useGlobalSearchUI() {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    throw new Error("useGlobalSearchUI must be used within GlobalSearchProvider");
  }
  return ctx;
}

/** Optional hook for components outside the provider tree (e.g. tests). */
export function useGlobalSearchUIOptional() {
  return useContext(GlobalSearchContext);
}

/** Route pages with an inline filter: wire ⌘K / top-bar search to that input. */
export function useRegisterPageSearchHandler(handler: PageSearchHandler | null) {
  const { registerPageSearchHandler } = useGlobalSearchUI();
  useEffect(() => {
    registerPageSearchHandler(handler);
    return () => registerPageSearchHandler(null);
  }, [handler, registerPageSearchHandler]);
}
